// A constellation drawn on its own patch of the night's sky: the weekday's
// colors, a grain, a faint haze and a dust of stars seeded by the word, with
// the figure fitted to the patch. Lines and stars keep one drawn size
// whatever the constellation's spread. Shared by the play menu's
// constellation cards and the Horoscope's signs.
import { PLANET_COLORS, planetAppearance, planetPath, planetsOf, type Constellation, type PlanetAppearance, type PlayLevel } from '@wits/core';
import { useId } from 'react';
import { seededRandom } from './weekdaySkies';

/** A figure in sky units: nodes in spelling order and lines as index pairs into them. */
export interface Figure {
  word: string;
  nodes: ReadonlyArray<{ x: number; y: number; type: 'star'; id?: number }>;
  /** A ringed planet's closing line comes last. */
  lines: ReadonlyArray<readonly [number, number]>;
  /** The planet the constellation ringed, when it did; without a look (a sign made before the planets), Saturn. */
  planet?: { x: number; y: number; look?: PlanetAppearance };
}

/** A played constellation's figure, its node ids resolved against the level. */
export function figureOf(level: PlayLevel, constellation: Constellation): Figure {
  const byId = new Map(level.nodes.map((n) => [n.id, n]));
  const placed = constellation.nodes.map((id) => byId.get(id)).filter((n) => n !== undefined);
  const index = new Map(placed.map((n, i) => [n.id, i]));
  const lines: Array<[number, number]> = [];
  for (const [a, b] of constellation.lines) {
    const ia = index.get(a);
    const ib = index.get(b);
    if (ia !== undefined && ib !== undefined) lines.push([ia, ib]);
  }
  const ringed = constellation.planet ? planetsOf(level).find((p) => p.id === constellation.planet!.id) : undefined;
  return { word: constellation.word, nodes: placed.map((n) => ({ x: n.x, y: n.y, type: n.type, id: n.id })), lines, ...(ringed ? { planet: { x: ringed.x, y: ringed.y, look: planetAppearance(level, ringed) } } : {}) };
}

interface Props {
  figure: Figure;
  /** The patch's size in its own units; the SVG scales to its box. */
  width: number;
  height: number;
  padX: number;
  padY: number;
  /** At most this many units per sky unit, so a small constellation is not blown up. */
  maxScale: number;
  /** The weekday sky's colors. */
  top: string;
  mid: string;
  dust?: number;
  className?: string;
}

export function ConstellationArt({ figure, width: W, height: H, padX, padY, maxScale, top, mid, dust = 44, className }: Props) {
  const id = useId().replace(/:/g, '');
  const points = figure.planet ? [...figure.nodes, figure.planet] : figure.nodes;
  const xs = points.map((n) => n.x);
  const ys = points.map((n) => n.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const spanX = Math.max(1, Math.max(...xs) - minX);
  const spanY = Math.max(1, Math.max(...ys) - minY);
  const s = Math.min((W - 2 * padX) / spanX, (H - 2 * padY) / spanY, maxScale);
  const ox = (W - spanX * s) / 2 - minX * s;
  const oy = (H - spanY * s) / 2 - minY * s;
  const at = (i: number) => ({ x: figure.nodes[i]!.x * s + ox, y: figure.nodes[i]!.y * s + oy });

  const rand = seededRandom(hash(figure.word));
  const specks = Array.from({ length: dust }, () => ({ x: rand() * W, y: rand() * H, r: 0.3 + rand() * 0.9, o: 0.18 + rand() * 0.6 }));
  const hazeAngle = Math.round(rand() * 60 - 30);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={className} role="img" aria-label={`The constellation ${figure.word}`}>
      <defs>
        <radialGradient id={`${id}-sky`} cx="30%" cy="25%" r="90%">
          <stop offset="0%" stopColor={mid} />
          <stop offset="100%" stopColor={top} />
        </radialGradient>
        <radialGradient id={`${id}-haze`}>
          <stop offset="0%" stopColor="#fff4d6" stopOpacity="0.09" />
          <stop offset="100%" stopColor="#fff4d6" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-glow`}>
          <stop offset="0%" stopColor="#fff4d6" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#fff4d6" stopOpacity="0" />
        </radialGradient>
        <filter id={`${id}-grain`} x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix values="0 0 0 0 1  0 0 0 0 0.96  0 0 0 0 0.84  0 0 0 0.55 0" />
        </filter>
      </defs>
      <rect width={W} height={H} fill={`url(#${id}-sky)`} />
      <rect width={W} height={H} filter={`url(#${id}-grain)`} opacity="0.07" />
      <ellipse cx={W / 2} cy={H / 2} rx={W * 0.62} ry={H * 0.2} fill={`url(#${id}-haze)`} transform={`rotate(${hazeAngle} ${W / 2} ${H / 2})`} />
      {specks.map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r={d.r} fill="var(--star-white)" opacity={d.o} />
      ))}
      {figure.lines.map(([a, b]) => {
        const pa = at(a);
        const pb = at(b);
        return <line key={`${a}-${b}`} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke="#8fa3b8" strokeOpacity="0.9" strokeWidth="1.6" strokeLinecap="round" />;
      })}
      {figure.planet && (
        // The ringed planet, at the stars' drawn size.
        <path
          d={planetPath(figure.planet.look ?? SATURN)}
          fill={PLANET_COLORS[(figure.planet.look ?? SATURN).kind]}
          fillRule="evenodd"
          transform={`translate(${figure.planet.x * s + ox} ${figure.planet.y * s + oy}) scale(${ICON_SCALE})`}
        />
      )}
      {figure.nodes.map((_, i) => {
        const p = at(i);
        return (
          <g key={i} transform={`translate(${p.x} ${p.y})`}>
            <circle r="12" fill={`url(#${id}-glow)`} />
            <path d={fourPoint(7.5, 1.35)} fill="var(--star-white)" />
          </g>
        );
      })}
    </svg>
  );
}

const SATURN: PlanetAppearance = { kind: 'saturn', flipped: false };

/** A star here is 7.5 units to a tip against 26 on the board; the planet takes the same share. */
const ICON_SCALE = 7.5 / 26;

/** A four-pointed star: outer radius o, waist i. */
export function fourPoint(o: number, i: number): string {
  return `M0 ${-o} L${i} ${-i} L${o} 0 L${i} ${i} L0 ${o} L${-i} ${i} L${-o} 0 L${-i} ${-i} Z`;
}

function hash(word: string): number {
  let h = 2166136261;
  for (const ch of word) h = Math.imul(h ^ ch.codePointAt(0)!, 16777619);
  return h >>> 0;
}
